"use client"

import * as React from "react"
import * as TabsPrimitive from "@radix-ui/react-tabs"
import { cn } from "../../utils"

/**
 * Platform-wide tab bar.
 *
 * Visual: underline-style — flat row of trigger buttons separated from the
 * content by a 1px border. The active trigger lifts to the primary brand
 * colour with a 2px underline that overlaps the row border (`-mb-px`).
 * Inactive triggers sit in muted text and lift to foreground on hover.
 *
 * Icons: pass them as children alongside the label, e.g.
 *
 *   <TabsTrigger value="scenarios"><Plus size={13} /> Scenarios</TabsTrigger>
 *
 * The trigger is `inline-flex` with `gap-1.5` so the icon + label spacing
 * works without any extra wrapper.
 *
 * Overflow: many tabs on a narrow viewport are scrolled inside the list
 * (`overflow-x-auto no-scrollbar`) instead of pushing the page wider.
 * `shrink-0` on the trigger is what makes that work — without it the
 * triggers would compress and the overflow wouldn't kick in.
 */

const Tabs = TabsPrimitive.Root

interface TabsListProps extends React.ComponentPropsWithoutRef<typeof TabsPrimitive.List> {
  variant?: "underline" | "pill" | "segment";
}

const TabsList = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.List>,
  TabsListProps
>(({ className, variant = "underline", ...props }, ref) => (
  <TabsPrimitive.List
    ref={ ref }
    className={ cn(
      variant === "underline" && "flex items-center gap-1 border-b border-border max-w-full overflow-x-auto no-scrollbar",
      variant === "pill" && "inline-flex items-center gap-1 p-1 bg-card rounded-xl border-0 max-w-full overflow-x-auto no-scrollbar shadow-xs",
      variant === "segment" && "inline-flex h-9 items-center justify-center rounded-lg bg-muted p-1 text-muted-foreground",
      className
    ) }
    { ...props }
  />
))
TabsList.displayName = TabsPrimitive.List.displayName

interface TabsTriggerProps extends React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger> {
  variant?: "underline" | "pill" | "segment";
}

const TabsTrigger = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Trigger>,
  TabsTriggerProps
>(({ className, variant = "underline", ...props }, ref) => (
  <TabsPrimitive.Trigger
    ref={ ref }
    className={ cn(
      "inline-flex shrink-0 items-center justify-center gap-1.5 whitespace-nowrap text-[13px] font-medium transition-all",
      "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
      "disabled:pointer-events-none disabled:opacity-50 select-none cursor-pointer border-0",
      variant === "underline" && [
        "relative px-3.5 py-2 border-b-2 border-transparent -mb-px text-muted-foreground hover:text-foreground",
        "data-[state=active]:border-primary data-[state=active]:text-foreground data-[state=active]:font-semibold data-[state=active]:z-10",
      ],
      variant === "pill" && [
        "px-3.5 py-1.5 rounded-lg text-muted-foreground hover:text-foreground font-medium",
        "data-[state=active]:bg-primary data-[state=active]:text-white data-[state=active]:font-semibold data-[state=active]:shadow-xs",
      ],
      variant === "segment" && [
        "px-3 py-1 text-xs rounded-md text-muted-foreground hover:text-foreground",
        "data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-xs",
      ],
      className
    ) }
    { ...props }
  />
))
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName

const TabsContent = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Content
    ref={ ref }
    className={ cn(
      "mt-3.5 ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
      className
    ) }
    { ...props }
  />
))
TabsContent.displayName = TabsPrimitive.Content.displayName

export { Tabs, TabsList, TabsTrigger, TabsContent }
export type { TabsListProps, TabsTriggerProps }
